/**
 * Shake detection for mobile devices using DeviceMotionEvent.
 */

type ShakeCallback = () => void;

let listening = false;
let callback: ShakeCallback | null = null;
let lastTrigger = 0;
const COOLDOWN_MS = 3000;
const CONSECUTIVE_REQUIRED = 3;
const WINDOW_MS = 500;

let readings: number[] = [];
let readingTimes: number[] = [];

/**
 * Start listening for shake gestures.
 * On iOS 13+, this must be called after a user gesture (e.g., button tap).
 */
export function startShakeDetection(_threshold: number, onShake: ShakeCallback): void {
  if (listening) return;

  // Respect reduced motion preference
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  // Feature detection
  if (!("DeviceMotionEvent" in window)) return;

  callback = onShake;

  window.addEventListener("devicemotion", handleMotion, { passive: true });
  listening = true;
}

export function stopShakeDetection(): void {
  if (!listening) return;
  window.removeEventListener("devicemotion", handleMotion);
  listening = false;
  callback = null;
  readings = [];
  readingTimes = [];
}

/**
 * Request iOS 13+ permission for DeviceMotionEvent.
 * Must be called from a user gesture (click/tap).
 * @returns true if permission granted or not needed
 */
export async function requestMotionPermission(): Promise<boolean> {
  const DME = DeviceMotionEvent as unknown as {
    requestPermission?: () => Promise<string>;
  };

  if (typeof DME.requestPermission !== "function") {
    return true; // Not iOS 13+ or permission not required
  }

  try {
    const result = await DME.requestPermission();
    return result === "granted";
  } catch {
    return false;
  }
}

let _threshold = 15;

export function setThreshold(t: number): void {
  _threshold = t;
}

function handleMotion(event: DeviceMotionEvent): void {
  const acc = event.accelerationIncludingGravity;
  if (!acc || acc.x == null || acc.y == null || acc.z == null) return;

  const magnitude = Math.sqrt(acc.x ** 2 + acc.y ** 2 + acc.z ** 2);
  const now = Date.now();

  if (magnitude > _threshold) {
    readings.push(magnitude);
    readingTimes.push(now);

    // Prune old readings outside the window
    while (readingTimes.length > 0 && now - readingTimes[0] > WINDOW_MS) {
      readings.shift();
      readingTimes.shift();
    }

    // Check if we have enough consecutive readings
    if (readings.length >= CONSECUTIVE_REQUIRED && now - lastTrigger > COOLDOWN_MS) {
      lastTrigger = now;
      readings = [];
      readingTimes = [];
      callback?.();
    }
  }
}
